import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase/config';
import { collection, query, where, getDocs, deleteDoc, doc } from 'firebase/firestore';
import { useAuth } from '../firebase/AuthContext';
import { inviteUser } from '../firebase/auth';
 
function Team() {
  const navigate = useNavigate();
  const { user, userData, orgData } = useAuth();
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteEmail, setInviteEmail] = useState('');
  const [invitePassword, setInvitePassword] = useState('');
  const [inviting, setInviting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [copied, setCopied] = useState(false);
 
  const isMaster = userData?.role === 'master';
 
  useEffect(() => {
    loadTeam();
  }, [userData]);
 
  const loadTeam = async () => {
    if (!userData?.orgId) return;
    try {
      const q = query(collection(db, 'users'), where('orgId', '==', userData.orgId));
      const snap = await getDocs(q);
      const members = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setTeamMembers(members);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };
 
  const handleInvite = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (!inviteEmail.trim()) return setError('Email is required.');
    if (invitePassword.length < 6) return setError('Password must be at least 6 characters.');
    setInviting(true);
    try {
      await inviteUser(inviteEmail, invitePassword, userData.orgId, userData.companyName);
      setSuccess(`${inviteEmail} has been added to your team.`);
      setInviteEmail('');
      setInvitePassword('');
      loadTeam();
    } catch (err) {
      if (err.message.includes('User limit')) {
        setError('Team limit reached. Max 5 users per account.');
      } else if (err.code === 'auth/email-already-in-use') {
        setError('An account with this email already exists.');
      } else {
        setError('Something went wrong. Please try again.');
      }
    }
    setInviting(false);
  };
 
  const handleRemove = async (memberId, memberEmail) => {
    if (memberId === user.uid) return setError("You can't remove yourself.");
    if (!window.confirm(`Remove ${memberEmail} from your team?`)) return;
    try {
      await deleteDoc(doc(db, 'users', memberId));
      setTeamMembers(prev => prev.filter(m => m.id !== memberId));
      setSuccess(`${memberEmail} has been removed.`);
    } catch (err) {
      setError('Error removing user. Please try again.');
    }
  };
 
  const handleCopySerial = () => {
    if (!orgData?.serialNumber) return;
    navigator.clipboard.writeText(orgData.serialNumber);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
 
  const usedSlots = teamMembers.length;
  const totalSlots = 5;
  const isActive = orgData?.active !== false; // default true if not set
  const planLabel = {
    starter: 'Cloud Starter',
    team: 'Cloud Team',
    local: 'MIA Local',
    local_pro: 'Local Pro',
  }[orgData?.plan] || 'Cloud Starter';
 
  // Suspended block — shown to all users if org is inactive
  if (!loading && !isActive) {
    return (
      <div style={styles.suspendedPage}>
        <div style={styles.suspendedCard}>
          <div style={styles.suspendedIcon}>⚠</div>
          <div style={styles.suspendedTitle}>Account suspended</div>
          <div style={styles.suspendedMsg}>
            Your MIA account has been suspended. Please contact support to reactivate.
          </div>
          {orgData?.serialNumber && (
            <div style={styles.suspendedSerial}>
              Serial: <strong>{orgData.serialNumber}</strong>
            </div>
          )}
          <a href="mailto:support@faiready.com" style={styles.suspendedBtn}>
            Contact support
          </a>
        </div>
      </div>
    );
  }
 
  return (
    <div style={styles.page}>
      {/* SIDEBAR */}
      <div style={styles.sidebar}>
        <div style={styles.logo}>
          <span style={styles.logoText}>M<span style={styles.logoBlue}>I</span>A</span>
          <div style={styles.logoSub}>Manufacturing<br />Inspection Assistant</div>
        </div>
        <nav style={styles.nav}>
          <div style={styles.navItem} onClick={() => navigate('/')}><span>⬆</span> Upload Print</div>
          <div style={styles.navItem}><span>◎</span> Review Bubbles</div>
          <div style={styles.navItem}><span>☰</span> FAI Document</div>
          <div style={styles.navItem}><span>✓</span> Final Inspection</div>
          <div style={styles.navItem}><span>↓</span> Exports</div>
          <div style={{ ...styles.navItem, ...styles.navActive }}><span>👥</span> Team</div>
        </nav>
      </div>
 
      {/* MAIN */}
      <div style={styles.main}>
        <div style={styles.header}>
          <div>
            <h1 style={styles.headerTitle}>Team Settings</h1>
            <p style={styles.headerSub}>{userData?.companyName} · {usedSlots} of {totalSlots} seats used</p>
          </div>
        </div>
 
        <div style={styles.content}>
 
          {/* ACCOUNT INFO — master only */}
          {isMaster && orgData && (
            <div style={styles.accountCard}>
              <div style={styles.cardTitle}>Account</div>
              <div style={styles.accountGrid}>
                <div style={styles.accountField}>
                  <div style={styles.accountLabel}>Serial number</div>
                  <div style={styles.serialRow}>
                    <span style={styles.serialNum}>{orgData.serialNumber || '—'}</span>
                    <button style={styles.copyBtn} onClick={handleCopySerial}>
                      {copied ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                </div>
                <div style={styles.accountField}>
                  <div style={styles.accountLabel}>Status</div>
                  <div style={styles.statusBadge(isActive)}>
                    {isActive ? '● Active' : '● Suspended'}
                  </div>
                </div>
                <div style={styles.accountField}>
                  <div style={styles.accountLabel}>Plan</div>
                  <div style={styles.planValue}>{planLabel}</div>
                </div>
              </div>
            </div>
          )}
 
          {/* SEAT USAGE */}
          <div style={styles.seatCard}>
            <div style={styles.seatLeft}>
              <div style={styles.seatTitle}>Team seats</div>
              <div style={styles.seatSub}>Your plan includes up to 5 users</div>
              <div style={styles.seatBar}>
                <div style={{ ...styles.seatFill, width: `${(usedSlots / totalSlots) * 100}%` }} />
              </div>
            </div>
            <div style={styles.seatCount}>
              <span style={styles.seatNum}>{usedSlots}</span>
              <span style={styles.seatTotal}>/ {totalSlots}</span>
            </div>
          </div>
 
          {/* INVITE FORM — master only */}
          {isMaster && usedSlots < totalSlots && (
            <div style={styles.inviteCard}>
              <div style={styles.cardTitle}>Invite a team member</div>
              <div style={styles.cardSub}>They'll use this email and password to log in to MIA.</div>
              <form onSubmit={handleInvite} style={styles.inviteForm}>
                <div style={styles.inviteRow}>
                  <div style={styles.fieldGroup}>
                    <label style={styles.label}>Email address</label>
                    <input
                      type="email"
                      style={styles.input}
                      placeholder="inspector@yourshop.com"
                      value={inviteEmail}
                      onChange={e => setInviteEmail(e.target.value)}
                      required
                    />
                  </div>
                  <div style={styles.fieldGroup}>
                    <label style={styles.label}>Temporary password</label>
                    <input
                      type="text"
                      style={styles.input}
                      placeholder="Min 6 characters"
                      value={invitePassword}
                      onChange={e => setInvitePassword(e.target.value)}
                      required
                    />
                  </div>
                  <button
                    type="submit"
                    style={{ ...styles.inviteBtn, ...(inviting ? styles.inviteBtnDisabled : {}) }}
                    disabled={inviting}
                  >
                    {inviting ? 'Adding...' : '+ Add User'}
                  </button>
                </div>
                {error && <div style={styles.error}>{error}</div>}
                {success && <div style={styles.successMsg}>{success}</div>}
              </form>
            </div>
          )}
 
          {!isMaster && (
            <div style={styles.infoBox}>Only the account owner can invite team members.</div>
          )}
 
          {usedSlots >= totalSlots && isMaster && (
            <div style={styles.infoBox}>Team is full — 5 of 5 seats used. Remove a user to add someone new.</div>
          )}
 
          {/* TEAM MEMBERS LIST */}
          <div style={styles.teamCard}>
            <div style={styles.cardTitle}>Team members</div>
            {loading ? (
              <div style={styles.loadingMsg}>Loading...</div>
            ) : (
              <div style={styles.memberList}>
                {teamMembers
                  .sort((a, b) => a.role === 'master' ? -1 : 1)
                  .map(member => (
                    <div key={member.id} style={styles.memberRow}>
                      <div style={styles.memberAvatar}>
                        {member.email.charAt(0).toUpperCase()}
                      </div>
                      <div style={styles.memberInfo}>
                        <div style={styles.memberEmail}>{member.email}</div>
                        <div style={styles.memberMeta}>
                          {member.id === user.uid ? 'You · ' : ''}
                          {member.role === 'master' ? 'Account Owner' : 'Team Member'}
                        </div>
                      </div>
                      <div style={styles.memberRight}>
                        <span style={{
                          ...styles.roleBadge,
                          background: member.role === 'master' ? '#eff6ff' : '#f9fafb',
                          color: member.role === 'master' ? '#2563EB' : '#6b7280',
                          border: `1px solid ${member.role === 'master' ? '#bfdbfe' : '#e5e7eb'}`
                        }}>
                          {member.role === 'master' ? 'Owner' : 'Member'}
                        </span>
                        {isMaster && member.id !== user.uid && (
                          <button
                            style={styles.removeBtn}
                            onClick={() => handleRemove(member.id, member.email)}
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </div>
 
        </div>
      </div>
    </div>
  );
}
 
const styles = {
  page: { display: 'flex', height: '100vh', background: '#f0f2f5', fontFamily: 'Inter, sans-serif' },
  sidebar: { width: '210px', background: '#0d1b2a', display: 'flex', flexDirection: 'column', flexShrink: 0 },
  logo: { padding: '24px 18px 20px', borderBottom: '1px solid #1a2a3a', display: 'flex', flexDirection: 'column', gap: '4px' },
  logoText: { fontSize: '32px', fontWeight: '800', color: '#fff', letterSpacing: '-1px', lineHeight: 1 },
  logoBlue: { color: '#3b82f6' },
  logoSub: { fontSize: '10px', color: '#475569', lineHeight: '1.4', marginTop: '4px' },
  nav: { padding: '12px 0', flex: 1 },
  navItem: { display: 'flex', alignItems: 'center', gap: '10px', padding: '9px 18px', fontSize: '13px', color: '#64748b', cursor: 'pointer' },
  navActive: { color: '#e2e8f0', fontWeight: '500', borderLeft: '3px solid #3b82f6', paddingLeft: '15px' },
  main: { flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' },
  header: { background: '#fff', borderBottom: '1px solid #e5e7eb', padding: '20px 32px' },
  headerTitle: { fontSize: '20px', fontWeight: '700', color: '#111827', margin: 0 },
  headerSub: { fontSize: '13px', color: '#9ca3af', margin: '3px 0 0' },
  content: { flex: 1, overflowY: 'auto', padding: '28px 32px', maxWidth: '700px' },
 
  // Account info card
  accountCard: { background: '#fff', borderRadius: '12px', border: '1px solid #e5e7eb', padding: '20px', marginBottom: '20px' },
  accountGrid: { display: 'flex', gap: '32px', marginTop: '12px', flexWrap: 'wrap' },
  accountField: { display: 'flex', flexDirection: 'column', gap: '6px' },
  accountLabel: { fontSize: '11px', fontWeight: '600', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' },
  serialRow: { display: 'flex', alignItems: 'center', gap: '10px' },
  serialNum: { fontSize: '14px', fontWeight: '600', color: '#111827', fontFamily: 'monospace', letterSpacing: '0.5px' },
  copyBtn: { fontSize: '11px', padding: '3px 10px', border: '1px solid #e5e7eb', borderRadius: '6px', background: '#f9fafb', color: '#6b7280', cursor: 'pointer', fontFamily: 'inherit' },
  statusBadge: (active) => ({
    fontSize: '13px', fontWeight: '600',
    color: active ? '#16a34a' : '#dc2626',
  }),
  planValue: { fontSize: '14px', fontWeight: '500', color: '#111827' },
 
  // Suspended screen
  suspendedPage: { display: 'flex', height: '100vh', alignItems: 'center', justifyContent: 'center', background: '#f0f2f5', fontFamily: 'Inter, sans-serif' },
  suspendedCard: { background: '#fff', borderRadius: '16px', border: '1px solid #e5e7eb', padding: '48px 40px', maxWidth: '420px', width: '100%', textAlign: 'center' },
  suspendedIcon: { fontSize: '36px', marginBottom: '16px' },
  suspendedTitle: { fontSize: '20px', fontWeight: '700', color: '#111827', marginBottom: '10px' },
  suspendedMsg: { fontSize: '14px', color: '#6b7280', lineHeight: '1.6', marginBottom: '16px' },
  suspendedSerial: { fontSize: '13px', color: '#9ca3af', marginBottom: '24px', fontFamily: 'monospace' },
  suspendedBtn: { display: 'inline-block', background: '#2563EB', color: '#fff', borderRadius: '9px', padding: '11px 24px', fontSize: '14px', fontWeight: '600', textDecoration: 'none' },
 
  seatCard: { background: '#fff', borderRadius: '12px', border: '1px solid #e5e7eb', padding: '20px', marginBottom: '20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  seatLeft: { flex: 1 },
  seatTitle: { fontSize: '14px', fontWeight: '600', color: '#111827', marginBottom: '3px' },
  seatSub: { fontSize: '12px', color: '#9ca3af', marginBottom: '10px' },
  seatBar: { height: '6px', background: '#f3f4f6', borderRadius: '3px', overflow: 'hidden', width: '200px' },
  seatFill: { height: '100%', background: '#2563EB', borderRadius: '3px', transition: 'width 0.3s ease' },
  seatCount: { display: 'flex', alignItems: 'baseline', gap: '2px' },
  seatNum: { fontSize: '28px', fontWeight: '800', color: '#111827' },
  seatTotal: { fontSize: '14px', color: '#9ca3af' },
  inviteCard: { background: '#fff', borderRadius: '12px', border: '1px solid #e5e7eb', padding: '20px', marginBottom: '20px' },
  cardTitle: { fontSize: '14px', fontWeight: '600', color: '#111827', marginBottom: '4px' },
  cardSub: { fontSize: '12px', color: '#9ca3af', marginBottom: '16px' },
  inviteForm: {},
  inviteRow: { display: 'flex', gap: '12px', alignItems: 'flex-end' },
  fieldGroup: { display: 'flex', flexDirection: 'column', gap: '6px', flex: 1 },
  label: { fontSize: '11px', fontWeight: '600', color: '#374151', textTransform: 'uppercase', letterSpacing: '0.05em' },
  input: { padding: '9px 12px', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '13px', color: '#111827', fontFamily: 'inherit', outline: 'none' },
  inviteBtn: { background: '#2563EB', border: 'none', borderRadius: '8px', padding: '9px 18px', fontSize: '13px', fontWeight: '600', color: '#fff', cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap', flexShrink: 0 },
  inviteBtnDisabled: { background: '#93c5fd', cursor: 'not-allowed' },
  error: { background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '10px 14px', color: '#dc2626', fontSize: '12.5px', marginTop: '12px' },
  successMsg: { background: '#f0fdf4', border: '1px solid #86efac', borderRadius: '8px', padding: '10px 14px', color: '#16a34a', fontSize: '12.5px', marginTop: '12px' },
  infoBox: { background: '#fefce8', border: '1px solid #fde68a', borderRadius: '10px', padding: '14px 18px', fontSize: '13px', color: '#92400e', marginBottom: '20px' },
  teamCard: { background: '#fff', borderRadius: '12px', border: '1px solid #e5e7eb', padding: '20px' },
  loadingMsg: { fontSize: '13px', color: '#9ca3af', padding: '12px 0' },
  memberList: { display: 'flex', flexDirection: 'column', gap: '2px' },
  memberRow: { display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 0', borderBottom: '1px solid #f9fafb' },
  memberAvatar: { width: '36px', height: '36px', borderRadius: '50%', background: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', fontWeight: '700', color: '#2563EB', flexShrink: 0 },
  memberInfo: { flex: 1 },
  memberEmail: { fontSize: '13.5px', fontWeight: '500', color: '#111827' },
  memberMeta: { fontSize: '11.5px', color: '#9ca3af', marginTop: '1px' },
  memberRight: { display: 'flex', alignItems: 'center', gap: '10px' },
  roleBadge: { fontSize: '11px', fontWeight: '600', padding: '3px 10px', borderRadius: '20px' },
  removeBtn: { background: 'transparent', border: '1px solid #fecaca', borderRadius: '6px', padding: '4px 10px', fontSize: '11.5px', color: '#dc2626', cursor: 'pointer', fontFamily: 'inherit' },
};
 
export default Team;